import type {
  ExecutionRate,
  FollowResponse,
  MyStrategySummary,
  PlaybookPayload,
  StrategyExecutionHistory,
} from './types';

export type Fetcher = (url: string, options?: RequestInit) => Promise<Response>;

type MyStrategiesOverviewApiPayload = {
  strategies?: MyStrategySummary[];
  playbooks_by_slug?: Record<string, PlaybookPayload | null>;
  playbook_errors_by_slug?: Record<string, string | null>;
  execution_rates_by_slug?: Record<string, ExecutionRate | null>;
  execution_rate_errors_by_slug?: Record<string, string | null>;
};

export type MyStrategiesOverview = {
  strategies: MyStrategySummary[];
  playbooksBySlug: Record<string, PlaybookPayload | null>;
  playbookErrorsBySlug: Record<string, string | null>;
  executionRatesBySlug: Record<string, ExecutionRate | null>;
  executionRateErrorsBySlug: Record<string, string | null>;
};

type FetchJsonOptions = {
  timeoutMs?: number;
  timeoutMessage?: string;
};

const MY_STRATEGIES_OVERVIEW_TIMEOUT_MS = 150_000;
const MY_STRATEGIES_OVERVIEW_TIMEOUT_MESSAGE =
  'We apologize for this downtime and are working to get it back up soon.';

export type PushSubscriptionStatus = {
  pushAvailable: boolean;
  availabilityReason: string | null;
  preferenceEnabled: boolean;
  activeSubscriptionCount: number;
  hasActiveSubscription: boolean;
  webPushConfigured: boolean;
  vapidPublicKey: string | null;
};

type MyStrategyFollowStatusesApiPayload = {
  follow_statuses_by_slug?: Record<string, 'following' | 'paused'>;
};

type FollowStatusesBySlug = Record<string, 'following' | 'paused'>;

const followStatusesCache = new Map<string, FollowStatusesBySlug>();
const followStatusesInFlight = new Map<string, Promise<FollowStatusesBySlug>>();

const normalizeFollowStatusesCacheKey = (cacheKey?: string | null): string | null => {
  const normalized = cacheKey?.trim().toLowerCase() || '';
  return normalized || null;
};

const normalizeFollowStatusesPayload = (
  payload: MyStrategyFollowStatusesApiPayload
): FollowStatusesBySlug =>
  payload.follow_statuses_by_slug && typeof payload.follow_statuses_by_slug === 'object'
    ? payload.follow_statuses_by_slug
    : {};

const parseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json();
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.error === 'string') return payload.error;
  } catch {
    // fall through
  }
  return `Request failed with status ${response.status}`;
};

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as { name?: unknown; message?: unknown };
  return maybeError.name === 'AbortError' || maybeError.message === 'The operation was aborted.';
};

const fetchJson = async <T>(
  url: string,
  init?: RequestInit,
  fetcher?: Fetcher,
  options: FetchJsonOptions = {}
): Promise<T> => {
  const executeFetch = fetcher ?? fetch;
  const timeoutMs =
    typeof options.timeoutMs === 'number' && Number.isFinite(options.timeoutMs) && options.timeoutMs > 0
      ? options.timeoutMs
      : null;
  const controller = timeoutMs ? new AbortController() : null;
  const timeoutId = controller
    ? window.setTimeout(() => controller.abort(), timeoutMs || 0)
    : null;
  const requestInit = controller
    ? {
        ...init,
        signal: controller.signal,
      }
    : init;

  try {
    const response = await executeFetch(url, requestInit);
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    return response.json() as Promise<T>;
  } catch (error) {
    if (controller?.signal.aborted || isAbortError(error)) {
      throw new Error(options.timeoutMessage || `Request timed out after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
  }
};

export const fetchMyStrategies = async (fetcher?: Fetcher): Promise<MyStrategySummary[]> => {
  const payload = await fetchJson<{ strategies: MyStrategySummary[] }>('/api/me/strategies', undefined, fetcher);
  return Array.isArray(payload.strategies) ? payload.strategies : [];
};

export const fetchMyStrategiesOverview = async (fetcher?: Fetcher): Promise<MyStrategiesOverview> => {
  const payload = await fetchJson<MyStrategiesOverviewApiPayload>(
    '/api/me/strategies/overview',
    undefined,
    fetcher,
    {
      timeoutMs: MY_STRATEGIES_OVERVIEW_TIMEOUT_MS,
      timeoutMessage: MY_STRATEGIES_OVERVIEW_TIMEOUT_MESSAGE,
    }
  );

  return {
    strategies: Array.isArray(payload.strategies) ? payload.strategies : [],
    playbooksBySlug:
      payload.playbooks_by_slug && typeof payload.playbooks_by_slug === 'object'
        ? payload.playbooks_by_slug
        : {},
    playbookErrorsBySlug:
      payload.playbook_errors_by_slug && typeof payload.playbook_errors_by_slug === 'object'
        ? payload.playbook_errors_by_slug
        : {},
    executionRatesBySlug:
      payload.execution_rates_by_slug && typeof payload.execution_rates_by_slug === 'object'
        ? payload.execution_rates_by_slug
        : {},
    executionRateErrorsBySlug:
      payload.execution_rate_errors_by_slug && typeof payload.execution_rate_errors_by_slug === 'object'
        ? payload.execution_rate_errors_by_slug
        : {},
  };
};

export const fetchMyStrategyFollowStatuses = async (
  fetcher?: Fetcher,
  options?: {
    cacheKey?: string | null;
    force?: boolean;
  }
): Promise<FollowStatusesBySlug> => {
  const cacheKey = normalizeFollowStatusesCacheKey(options?.cacheKey);

  if (!options?.force && cacheKey) {
    const cached = followStatusesCache.get(cacheKey);
    if (cached) return cached;

    const inFlight = followStatusesInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  }

  const request = fetchJson<MyStrategyFollowStatusesApiPayload>(
    '/api/me/strategies/follow-statuses',
    undefined,
    fetcher
  )
    .then((payload) => {
      const normalized = normalizeFollowStatusesPayload(payload);
      if (cacheKey) {
        followStatusesCache.set(cacheKey, normalized);
      }
      return normalized;
    })
    .finally(() => {
      if (cacheKey) {
        followStatusesInFlight.delete(cacheKey);
      }
    });

  if (cacheKey) {
    followStatusesInFlight.set(cacheKey, request);
  }

  return request;
};

export const getCachedMyStrategyFollowStatuses = (
  cacheKey?: string | null
): FollowStatusesBySlug | null => {
  const normalizedCacheKey = normalizeFollowStatusesCacheKey(cacheKey);
  if (!normalizedCacheKey) return null;
  return followStatusesCache.get(normalizedCacheKey) ?? null;
};

export const primeCachedMyStrategyFollowStatuses = (
  cacheKey: string | null | undefined,
  statuses: FollowStatusesBySlug
): void => {
  const normalizedCacheKey = normalizeFollowStatusesCacheKey(cacheKey);
  if (!normalizedCacheKey) return;
  followStatusesCache.set(normalizedCacheKey, statuses);
};

export const invalidateCachedMyStrategyFollowStatuses = (
  cacheKey?: string | null
): void => {
  const normalizedCacheKey = normalizeFollowStatusesCacheKey(cacheKey);
  if (!normalizedCacheKey) return;
  followStatusesCache.delete(normalizedCacheKey);
  followStatusesInFlight.delete(normalizedCacheKey);
};

export const fetchStrategyPlaybook = async (slug: string, fetcher?: Fetcher): Promise<PlaybookPayload> => {
  const payload = await fetchJson<{ playbook: PlaybookPayload }>(
    `/api/me/strategies/${encodeURIComponent(slug)}/playbook`,
    undefined,
    fetcher
  );
  return payload.playbook;
};

export const fetchExecutionRate = async (slug: string, fetcher?: Fetcher): Promise<ExecutionRate> => {
  const payload = await fetchJson<{ execution_rate: ExecutionRate }>(
    `/api/me/strategies/${encodeURIComponent(slug)}/execution-rate`,
    undefined,
    fetcher
  );
  return payload.execution_rate;
};

export const fetchExecutionHistory = async (
  slug: string,
  fetcher?: Fetcher
): Promise<StrategyExecutionHistory> => {
  const payload = await fetchJson<{ execution_history: StrategyExecutionHistory }>(
    `/api/me/strategies/${encodeURIComponent(slug)}/execution-history`,
    undefined,
    fetcher
  );
  return payload.execution_history;
};

export const followStrategy = async (
  slug: string,
  allocatedCapital: number | null,
  fetcher?: Fetcher
): Promise<FollowResponse> => {
  const payload = await fetchJson<{ follow: FollowResponse }>(
    `/api/me/strategies/${encodeURIComponent(slug)}/follow`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allocated_capital: allocatedCapital }),
    },
    fetcher
  );
  return payload.follow;
};

export const updateFollow = async (
  slug: string,
  patch: {
    status?: 'following' | 'paused' | 'unfollowed';
    allocated_capital?: number | null;
    followed_at?: string;
  },
  fetcher?: Fetcher
): Promise<FollowResponse> => {
  const payload = await fetchJson<{ follow: FollowResponse }>(
    `/api/me/strategies/${encodeURIComponent(slug)}/follow`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
    fetcher
  );
  return payload.follow;
};

export const acknowledgePlaybookAction = async (
  actionId: number,
  status: 'executed' | 'skipped',
  fetcher?: Fetcher
): Promise<{ id: number; status: string; executed_at: string | null }> => {
  const payload = await fetchJson<{ action: { id: number; status: string; executed_at: string | null } }>(
    `/api/me/actions/${actionId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    },
    fetcher
  );
  return payload.action;
};

export const fetchPushSubscriptionStatus = async (fetcher?: Fetcher): Promise<PushSubscriptionStatus> => {
  const payload = await fetchJson<{ push: PushSubscriptionStatus }>(
    '/api/me/push/subscriptions',
    undefined,
    fetcher
  );
  return payload.push;
};

export const savePushSubscription = async (
  input: {
    subscription: Pick<PushSubscriptionJSON, 'endpoint' | 'keys'>;
    permissionState: string | null;
    platform: string | null;
    userAgent: string | null;
  },
  fetcher?: Fetcher
): Promise<PushSubscriptionStatus> => {
  const payload = await fetchJson<{ push: PushSubscriptionStatus }>(
    '/api/me/push/subscriptions',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription: input.subscription,
        permission_state: input.permissionState,
        platform: input.platform,
        user_agent: input.userAgent,
      }),
    },
    fetcher
  );
  return payload.push;
};

export const deletePushSubscription = async (
  endpoint: string,
  permissionState: string | null,
  fetcher?: Fetcher
): Promise<PushSubscriptionStatus> => {
  const payload = await fetchJson<{ push: PushSubscriptionStatus }>(
    '/api/me/push/subscriptions',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint,
        permission_state: permissionState,
      }),
    },
    fetcher
  );
  return payload.push;
};
